import { useCallback, useEffect, useState } from 'react';
import { useMe } from '../../contexts/MeContext';
import { hubService, type BotWalletView } from '../../services/hub.service';
import { conflictMessage, formatMnt, txLabel, validateAdjust, validateTopUp, type FormErrors } from '../../lib/walletView';

/**
 * The bot's prepaid AI balance and its ledger (LLM usage phase 1a). Members see it; global admins also record
 * top-ups (a customer's bank transfer, with its bank / invoice reference) and corrections.
 */
export function WalletCard({ system, botId, token }: { system: string; botId: string; token: string | null }) {
  const me = useMe();
  const isGlobalAdmin = !!me && me.kind !== 'service' && me.isGlobalAdmin === true;
  const [wallet, setWallet] = useState<BotWalletView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<'topups' | 'adjustments'>('topups');
  const [form, setForm] = useState({ amount: '', ref: '', note: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    hubService.getWallet(system, botId, token).then(setWallet).catch((e) => setError(String(e?.message ?? e)));
  }, [system, botId, token]);
  useEffect(load, [load]);

  async function submit() {
    const errs = kind === 'topups' ? validateTopUp(form) : validateAdjust(form);
    setErrors(errs);
    setNotice(null);
    if (Object.keys(errs).length) return;
    setSaving(true);
    const r = await hubService.recordWalletMovement(
      kind, system, botId,
      { amountMnt: Number(form.amount), ref: form.ref.trim(), ...(form.note.trim() ? { note: form.note.trim() } : {}) },
      token,
    );
    setSaving(false);
    if (r.ok) {
      setNotice(r.duplicate ? 'Already recorded — nothing changed' : `Recorded. Balance: ${formatMnt(r.balanceMnt)}`);
      setForm({ amount: '', ref: '', note: '' });
      load();
    } else {
      setNotice(conflictMessage(r.status) ?? `Failed (${r.status})`);
    }
  }

  return (
    <section className="mb-8 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-gray-900">AI balance</h2>
        <span className="text-2xl font-bold text-gray-900">{wallet ? formatMnt(wallet.balanceMnt) : '…'}</span>
      </div>
      <p className="mt-1 text-xs text-gray-500">Cloud AI models are paid from this balance; the local AI is not charged.</p>
      {error && <div className="mt-2 text-sm text-red-700">{error}</div>}

      {isGlobalAdmin && (
        <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
          <div className="mb-2 flex gap-3 text-sm">
            <button type="button" onClick={() => setKind('topups')} className={kind === 'topups' ? 'font-semibold' : 'text-gray-500'}>Record a top-up</button>
            <button type="button" onClick={() => setKind('adjustments')} className={kind === 'adjustments' ? 'font-semibold' : 'text-gray-500'}>Correction</button>
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              Amount (₮)
              <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputMode="decimal"
                className="rounded border border-gray-300 px-2 py-1.5 text-sm" />
              {errors.amount && <span className="text-red-600">{errors.amount}</span>}
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              {kind === 'topups' ? 'Bank / invoice reference' : 'Reference'}
              <input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm" />
              {errors.ref && <span className="text-red-600">{errors.ref}</span>}
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs text-gray-600">
              {kind === 'topups' ? 'Note (optional)' : 'Reason'}
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm" />
              {errors.note && <span className="text-red-600">{errors.note}</span>}
            </label>
            <button type="button" onClick={submit} disabled={saving}
              className="mt-5 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {notice && <div className="mt-2 text-sm text-gray-700">{notice}</div>}
        </div>
      )}

      {wallet && wallet.transactions.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1">Date</th><th>Type</th><th className="text-right">Amount</th><th className="text-right">Balance</th><th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {wallet.transactions.map((t) => (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="py-1">{new Date(t.createdAt).toLocaleString()}</td>
                <td>{txLabel(t.type)}</td>
                <td className="text-right">{formatMnt(t.amountMnt)}</td>
                <td className="text-right">{formatMnt(t.balanceAfterMnt)}</td>
                <td className="text-gray-500" title={t.note ?? ''}>{t.ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
